import { StandardOptionProps } from "ui/forms/Filter";

import api from "API";
import * as Params from "API/params";

import { Document, DocumentFile, GetDocument, GetDocuments } from "./payloads";

export const baseEndpoint = "/organisations/:org";
export const documentsEndpoint = `${baseEndpoint}/documents/`;

const extendedEndpoints = api.injectEndpoints({
  endpoints: (builder) => ({
    documents: builder.query<GetDocuments, Params.GenericParamMap>({
      query: (query) => ({
        url: documentsEndpoint,
        method: "GET",
        params: {
          page: query.page,
          page_size: query.page_size,
          category: Params.toArray(query.doc_category),
          entities: Params.toArray(query.entity),
          tags: Params.toArray(query.doc_tag),
          uploaded_by: Params.toArray(query.doc_uploaded_by),
          uploaded_at: Params.toString(query.doc_uploaded_at),
          valid_from: Params.toString(query.valid_from),
          valid_to: Params.toString(query.valid_to),
        },
      }),
    }),
    document: builder.query<GetDocument, Params.GenericParamMap>({
      query: ({ documentId }) => ({
        url: documentsEndpoint + `${documentId}`,
        method: "GET",
      }),
      keepUnusedDataFor: 0,
    }),
    documentDownload: builder.query<DocumentFile, Params.GenericParamMap>({
      query: ({ documentId }) => {
        return {
          url: documentsEndpoint + `${documentId}/download`,
        };
      },
    }),
    documentDelete: builder.mutation<DocumentFile, Params.GenericParamMap>({
      query: ({ documentId }) => {
        return {
          url: documentsEndpoint + `${documentId}`,
          method: "DELETE",
        };
      },
    }),
    documentUpload: builder.mutation<Document, Params.GenericParamMap>({
      query: ({
        file,
        description,
        category,
        className,
        entities,
        tags,
        validFrom,
        validTo,
      }) => {
        return {
          url: documentsEndpoint,
          method: "POST",
          body: {
            file,
            description,
            category,
            entities,
            tags,
            calculation_reference_classname: className,
            valid_from: validFrom,
            valid_to: validTo,
          },
        };
      },
    }),
    documentUpdate: builder.mutation<Document, Params.GenericParamMap>({
      query: ({
        documentId,
        description,
        category,
        entities,
        tags,
        validFrom,
        validTo,
        fileId,
      }) => {
        return {
          url: documentsEndpoint + `${documentId}`,
          method: "PATCH",
          body: {
            description,
            category,
            entities,
            tags,
            valid_from: validFrom,
            valid_to: validTo,
            file: fileId,
          },
        };
      },
    }),
    calculationReferenceClassNames: builder.query<
      StandardOptionProps["options"],
      void
    >({
      query: () => ({
        url: baseEndpoint + "/calculation-reference-classnames",
        method: "GET",
      }),
      keepUnusedDataFor: 60,
      transformResponse(original: string[]) {
        return original.map((item) => ({ label: item, value: item }));
      },
    }),
    referenceDocumentValidate: builder.mutation<void, Params.GenericParamMap>({
      query: ({ calculationReferenceClassname, fileId }) => {
        return {
          url: documentsEndpoint + "validate/",
          method: "POST",
          body: {
            calculation_reference_classname: calculationReferenceClassname,
            file: fileId,
          },
        };
      },
    }),
  }),
});

export const {
  useDocumentsQuery,
  useDocumentQuery,
  useDocumentDownloadQuery,
  useLazyDocumentDownloadQuery,
  useDocumentDeleteMutation,
  useDocumentUploadMutation,
  useDocumentUpdateMutation,
  useCalculationReferenceClassNamesQuery,
  useReferenceDocumentValidateMutation,
} = extendedEndpoints;
